import { useTheme } from '@emotion/react';
import { darken, getContrastRatio, lighten } from '@mui/system';
import { CHALLENGE_NULL_COLOR } from '../../Challenge/ChallengeView/ChallengeColorBar';
import { useContext } from 'react';
import PortfolioItemContext from '../PortfolioItemContext';

const MINIMUM_CONTRAST_THRESHOLD_LIGHT_MODE = 2;
const MINIMUM_CONTRAST_THRESHOLD_DARK_MODE = 4;

const increaseContrastToMinimum = (
  portfolioItemColor: string,
  paperColor: string,
  increaseContrastFunction: (color: string, coefficient: number) => string,
  minimumContrastThreshold: number,
): string => {
  // Coefficient as in the lighten/darken coefficient
  let coefficientLowerBound = 0;
  let coefficientUpperBound = 1;

  let newColor = null;

  // This essentially is a 20-step binary search for a sufficiently high
  // contrast color
  for (let i = 0; i < 20; i++) {
    const coefficientCandidate =
      (coefficientLowerBound + coefficientUpperBound) / 2;
    const colorCandidate = increaseContrastFunction(
      portfolioItemColor,
      coefficientCandidate,
    );
    const contrastRatio = getContrastRatio(colorCandidate, paperColor);

    if (contrastRatio >= minimumContrastThreshold) {
      newColor = colorCandidate;
      // Try to get a smaller but still sufficient coefficient
      coefficientUpperBound = coefficientCandidate;
    } else {
      // Need greater contrast increase
      coefficientLowerBound = coefficientCandidate;
    }
  }

  return newColor!;
};

const useSectionNameColor = (): string | null => {
  const theme = useTheme();
  const { portfolioItem } = useContext(PortfolioItemContext);

  if (
    portfolioItem.color === null ||
    portfolioItem.color === CHALLENGE_NULL_COLOR
  ) {
    return null;
  }

  const contrastRatio = getContrastRatio(
    portfolioItem.color,
    theme.palette.background.paper,
  );

  const minimumContrastThreshold = theme.isDark
    ? MINIMUM_CONTRAST_THRESHOLD_DARK_MODE
    : MINIMUM_CONTRAST_THRESHOLD_LIGHT_MODE;

  if (contrastRatio < minimumContrastThreshold) {
    return increaseContrastToMinimum(
      portfolioItem.color,
      theme.palette.background.paper,
      theme.isDark ? lighten : darken,
      minimumContrastThreshold,
    );
  }

  return portfolioItem.color;
};

export default useSectionNameColor;
